import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@multaqa/ui/components/ui/select'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useState } from 'react'
import type { DocumentSequenceResource, ResetFrequency } from './api'
import { useUpdateSequence } from './api'

const RESET_FREQUENCIES: ResetFrequency[] = ['never', 'yearly', 'monthly']

export function CompanySequenceDialog({
  sequence,
  groupId,
  companyId,
  open,
  onOpenChange,
}: {
  sequence: DocumentSequenceResource
  groupId: string
  companyId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const update = useUpdateSequence()
  const [prefix, setPrefix] = useState(sequence.prefix)
  const [padding, setPadding] = useState(String(sequence.padding))
  const [reset, setReset] = useState<ResetFrequency>(
    isResetFrequency(sequence.reset_frequency) ? sequence.reset_frequency : 'never',
  )
  const [message, setMessage] = useState<string | null>(null)

  function save() {
    const value = Number(padding)
    if (!prefix.trim()) return setMessage(t('companies.sequences.prefixRequired'))
    if (!Number.isInteger(value) || value < 1 || value > 20)
      return setMessage(t('companies.sequences.paddingInvalid'))
    update.mutate(
      {
        groupId,
        companyId,
        sequenceId: sequence.id,
        input: { prefix: prefix.trim(), padding: value, reset_frequency: reset },
      },
      {
        onSuccess: () => {
          onOpenChange(false)
          toast.success(t('state.saved'))
        },
        onError: () => setMessage(t('companies.sequences.saveFailed')),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('action.cancel')}
        className="max-w-md"
        data-testid="company-sequence-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('companies.sequences.editTitle')}</DialogTitle>
          <DialogDescription>{sequence.document_type}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          noValidate
        >
          <DialogBody className="grid gap-4">
            {message ? (
              <p className="text-small text-danger" role="alert">
                {message}
              </p>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="company-sequence-prefix">{t('companies.sequences.prefix')}</Label>
              <Input
                id="company-sequence-prefix"
                value={prefix}
                dir="ltr"
                onChange={(event) => setPrefix(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="company-sequence-padding">{t('companies.sequences.padding')}</Label>
              <Input
                id="company-sequence-padding"
                type="number"
                min="1"
                max="20"
                numeric
                value={padding}
                onChange={(event) => setPadding(event.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="company-sequence-reset">{t('companies.sequences.resetLabel')}</Label>
              <Select
                value={reset}
                onValueChange={(value) => {
                  if (isResetFrequency(value)) setReset(value)
                }}
              >
                <SelectTrigger id="company-sequence-reset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESET_FREQUENCIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`companies.sequences.reset.${value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </DialogBody>
          <DialogFooter showCloseButton={false}>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={update.isPending}
            >
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              loading={update.isPending}
              disabled={update.isPending}
              data-testid="company-sequence-submit"
            >
              {t('companies.sequences.submitEdit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function isResetFrequency(value: string): value is ResetFrequency {
  return (RESET_FREQUENCIES as string[]).includes(value)
}
